import moment from "moment";
import { RelationType } from "../relationType";
import BuilderModel from "../tracked/builderModel";
import { ConditionalFieldDefinition } from "./conditionalFieldDefinition";
import ConditionalRule from "./conditionalRule";
import ConditionalRuleFactory from "./conditionalRuleFactory";
import ConditionalRuleSetFactory from "./conditionalRuleSetFactory";
import { DisplayRuleType } from "./displayRuleType";
import { JoinType } from "./joinType";

export default abstract class ConditionalRuleSet extends BuilderModel {
  conditionalFieldDefinition: ConditionalFieldDefinition;

  join_type: JoinType;
  sort_order: number;
  display_type: DisplayRuleType = null;
  conditional_value: any;
  conditional_value_formula?: any;
  fieldName: string;
  default_value: any;
  conditional_rules: { [key: string]: ConditionalRule | ConditionalRuleSet} = {};
  except = ['object_model', 'conditional_rules', 'conditionalFieldDefinition', 'createEmbeddedObject', 'embeddedObjects', 'embeddedHashes', 'fieldName', 'objectCreatedAt'];
  objectCreatedAt: Date;
  embeddedObjects: string[] = [];
  embeddedHashes: string[] = [];

  protected constructor(fieldName: string, conditionalFieldDefinition: ConditionalFieldDefinition) {
    super();
    this.conditionalFieldDefinition = conditionalFieldDefinition;
    this.fieldName = fieldName;
    this.objectCreatedAt = new Date();
    if (conditionalFieldDefinition.defaultConditionalValue instanceof Function) {
      this.createEmbeddedObject['conditional_value'] = conditionalFieldDefinition.defaultConditionalValue;
      this.except.push('conditional_value');
      this.embeddedObjects.push('conditional_value');
      this.embeddedHashes.push('conditional_value');
    }
  }

  abstract createNew(fieldName: string, conditionalFieldDefinition: ConditionalFieldDefinition): ConditionalRuleSet;

  abstract createNewRule(): ConditionalRule;

  isMoreRecent(otherRule: this): boolean {
    return moment(this.u_at || this.objectCreatedAt).isAfter(moment(otherRule.u_at || otherRule.objectCreatedAt));
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'conditional_rules': { type: ConditionalRuleSet, relationType: RelationType.dictionary }
    };
    for (const objKey of this.embeddedObjects) {
      models[objKey] = { type: Object, relationType: RelationType.singleObject };
    }
    return models;
  }

  get EMBEDDED_HASHES(): string[] {
    return this.embeddedHashes;
  }

  createEmbeddedObject = {
    conditional_rules: (parent: this, json): ConditionalRule | ConditionalRuleSet => {
      if (json["conditional_rules"]) {
        return ConditionalRuleSetFactory.createForRuleSet(parent, json, true, false, this.fieldName);
      } else {
        return ConditionalRuleFactory.create(parent, json, true);
      }
    },
  };

  get conditionalRules(): Array<ConditionalRule | ConditionalRuleSet> {
    return Object.keys(this.conditional_rules).map(key => this.conditional_rules[key]);
  }

  get conditionalRulesArray(): Array<ConditionalRule | ConditionalRuleSet> {
    return this.conditionalRules
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get completeConditionalRulesArray(): Array<ConditionalRule | ConditionalRuleSet> {
    return this.conditionalRulesArray.filter(r => r.isComplete);
  }

  getDependenciesFor(controlId: string) {
    return this.conditionalRules
      .filter(r => r.matchesDependenciesFor(controlId));
  }

  matchesDependenciesFor(controlId: string): boolean {
    return this.conditionalRules.filter(r => r.matchesDependenciesFor(controlId)).length > 0;
  }

  nextRuleSortOrder(): number {
    let position = 0;
    Object.keys(this.conditional_rules)
      .map(key => this.conditional_rules[key])
      .forEach((element, i) => {
        if (i >= position) {
          position = i + 1;
        }
      });
    return position;
  }

  addNewRule(trackCreation: boolean = true): ConditionalRule {
    const newRule = ConditionalRuleFactory.create(this, {}, this.isAttached, true);
    if (trackCreation) {
      this.trackCreationDeletion(newRule.client_id, null, newRule.asJson(), newRule);
    }
    return newRule;
  }

  addNewRuleSet(trackCreation: boolean = true): ConditionalRuleSet {
    const newRuleSet = ConditionalRuleSetFactory.createForRuleSet(this, {}, this.isAttached, true, this.fieldName, true);
    if (trackCreation) {
      this.trackCreationDeletion(newRuleSet.client_id, null, newRuleSet.asJson(), newRuleSet);
    }
    return newRuleSet;
  }

  get isComplete(): boolean {
    return Object.keys(this.conditional_rules).map(key => this.conditional_rules[key])
      .filter(r => !r.isComplete).length === 0;
  }

  get isValid(): boolean {
    return Object.values(this.conditional_rules)
                 .filter(r => !r.isValid).length === 0;
  }

  toString(): string {
    return this.nestRuleString(this.mapConditionals(r => r.toString()));
  }

  toHtmlString(form): string {
    return this.nestRuleString(this.mapConditionals(r => r.toHtmlString(form), true));
  }

  private nestRuleString(conditionalString: string):string {
    if (this.parent instanceof ConditionalRuleSet) {
      return `(${conditionalString})`;
    } else {
      return conditionalString;
    }
  }

  private mapConditionals(action: (r: ConditionalRule) => string, isHtml: boolean = false): string {
    let startString = '';
    let orStartString = '';
    let endString = '';
    if (isHtml) {
      startString = '<em class="crayon blue and">';
      orStartString = '<em class="crayon green or">';
      endString = '</em>';
    }
    return `${this.conditionalRulesArray.map(action).join(this.join_type === JoinType.and ? ` ${startString}and${endString} ` : ` ${orStartString}or${endString} `)}`;
  }

  get conditionalFieldFriendlyName(): string {
    const friendlyName = this.conditionalFieldDefinition.friendlyName;
    if (typeof friendlyName === "string") {
      return friendlyName;
    } else {
      return friendlyName(this);
    }
  }

  toStringLong(): string {
    return `${this.conditionalFieldFriendlyName} ${this.parent.referenceNameOrDefault} if ${this.completeConditionalRulesArray.map(r => r.toString()).join( this.join_type === JoinType.and ? ' and ' : ' or ')}`;
  }

  get rootName(): string {
    if (this.parent instanceof ConditionalRuleSet) {
      return 'conditional_rules';
    } else {
      return `${this.fieldName}_conditional_rule_sets`;
    }
  }

  get rootModelDictName(): string {
    if (this.parent instanceof ConditionalRuleSet) {
      return 'conditional_rules';
    } else {
      return 'conditional_rule_sets';
    }
  }

  get elementName(): string {
    return 'conditional_rule_set';
  }

  get childRootName(): string {
    return 'conditional_rules';
  }

  attachToParentCallback(): void {
    this.sort_order = this.sort_order === undefined ? this.parent.nextRuleSetSortOrder(this.fieldName) : this.sort_order;
  }

  get hasOne(): boolean {
    return this.conditionalRulesArray.length === 1;
  }

  delete(): void {
    if (this.parent instanceof ConditionalRuleSet && this.parent.hasOne) {
      this.parent.destroy();
    } else {
      super.destroy();
    }
  }

  forEachRule(callback, recurse: boolean = true) {
    this.conditionalRulesArray.forEach(ruleOrSet => {
      if ('forEachRule' in ruleOrSet) {
        ruleOrSet.forEachRule(callback, recurse)
      } else {
        callback(ruleOrSet)
      }
    })
  }
}